import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import {
  FileDropzone,
  type ExistingFile,
  type PendingFile,
} from "@/components/ui/customs/file-dropzone";
import { useCreateImporter, useGetOneImporter, useUpdateImporter } from "@/api/importer.api";
import { useCreateFileBo } from "@/api/file.api";
import { ImporterDto } from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import {
  importerFormSchema,
  IMPORTER_DEFAULT_VALUES,
  type ImporterFormData,
} from "./importerFormSchema";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { useTranslation } from "react-i18next";

const IMPORTER_FILE_TYPES = [
  FileTypeEnum.IMPORTER_LICENCE,
  FileTypeEnum.IMPORTER_BASIC_PERMIT,
  FileTypeEnum.IMPORTER_OTHER,
];
const IMPORTER_FILE_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
const IMPORTER_MAX_FILES = 10;

type Props = {
  open: boolean;
  onClose: () => void;
  importer?: ImporterDto | null;
};

export const ImporterFormModal = ({ open, onClose, importer }: Props) => {
  const { t } = useTranslation(["boCatalog", "common"]);
  const isEdit = !!importer;

  const { mutateAsync: createImporter, isPending: isCreating } = useCreateImporter();
  const { mutateAsync: updateImporter, isPending: isUpdating } = useUpdateImporter();
  const { mutateAsync: createFile } = useCreateFileBo();
  const { data: importerDetail } = useGetOneImporter(importer?.id ?? "");

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const isPending = isCreating || isUpdating || isUploading;

  const { control, handleSubmit, reset } = useForm<ImporterFormData>({
    resolver: zodResolver(importerFormSchema),
    defaultValues: IMPORTER_DEFAULT_VALUES,
  });

  const currentFiles: ExistingFile[] = (importerDetail?.files ?? []).map((f) => ({
    fileId: f.id,
    fileName: f.name,
    fileUrl: f.path,
    fileType: f.type as FileTypeEnum,
  }));

  useEffect(() => {
    if (!open) return;
    setPendingFiles([]);
    setFilesToRemove([]);
    if (importer) {
      reset({
        name: importer.name,
        email: importer.email,
        phone: importer.phone,
        address: importer.address,
        city: importer.city,
        state: importer.state,
        zip: importer.zip,
        country: importer.country,
        ttbPermit: importer.ttbPermit,
        taxId: importer.taxId ?? "",
        einNumber: importer.einNumber ?? "",
        duns: importer.duns ?? "",
        newFiles: [],
        filesToDelete: [],
      });
    } else {
      reset(IMPORTER_DEFAULT_VALUES);
    }
  }, [open, importer, reset]);

  const uploadPendingFile = async (pf: PendingFile): Promise<string> => {
    const contentType = pf.file.type || "application/octet-stream";
    const { uploadUrl, fileId } = await createFile({
      fileName: pf.file.name,
      type: pf.fileType,
      contentType,
    });

    await fetch(uploadUrl, {
      method: "PUT",
      body: pf.file,
      headers: { "Content-Type": contentType },
    });

    return fileId;
  };

  const onSubmit = async (data: ImporterFormData) => {
    try {
      setIsUploading(true);

      const uploadedFileIds = await Promise.all(pendingFiles.map(uploadPendingFile));

      const { newFiles: _nf, filesToDelete: _ftd, ...payload } = data;
      const newFiles = uploadedFileIds.length > 0 ? uploadedFileIds : undefined;
      const filesToDelete = filesToRemove.length > 0 ? filesToRemove : undefined;

      if (isEdit) {
        await updateImporter({
          id: importer.id,
          payload: { ...payload, newFiles, filesToDelete },
        });
      } else {
        await createImporter({ ...payload, newFiles });
      }
      onClose();
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t("importerFormModal.editTitle") : t("importerFormModal.createTitle")}
      size="md"
      contentClassName="max-w-[600px] max-h-[80vh]"
      footer={
        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:space-x-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" form="importerForm" disabled={isPending}>
            {isPending ? (
              <Loader2 className="animate-spin" />
            ) : isEdit ? (
              t("importerFormModal.save")
            ) : (
              t("importerFormModal.create")
            )}
          </Button>
        </div>
      }
    >
      <form id="importerForm" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <ControlledUnifiedInput
              control={control}
              name="name"
              label={t("importerFormModal.nameLabel")}
              placeholder={t("importerFormModal.namePlaceholder")}
              wrapperClassName="col-span-2"
            />
            <ControlledUnifiedInput
              control={control}
              name="email"
              label={t("importerFormModal.emailLabel")}
              placeholder={t("importerFormModal.emailPlaceholder")}
            />
            <ControlledUnifiedInput
              control={control}
              name="phone"
              label={t("importerFormModal.phoneLabel")}
              placeholder="+1 234 567 890"
            />
            <ControlledUnifiedInput
              control={control}
              name="address"
              label={t("importerFormModal.addressLabel")}
              placeholder="123 Main Street"
              wrapperClassName="col-span-2"
            />
            <ControlledUnifiedInput
              control={control}
              name="city"
              label={t("importerFormModal.cityLabel")}
              placeholder="New York"
            />
            <ControlledUnifiedInput
              control={control}
              name="state"
              label={t("importerFormModal.stateLabel")}
              placeholder="NY"
            />
            <ControlledUnifiedInput
              control={control}
              name="zip"
              label={t("importerFormModal.zipLabel")}
              placeholder="10001"
            />
            <ControlledCountryComboboxCountryCode
              control={control}
              name="country"
              placeholder=""
              label={t("importerFormModal.countryLabel")}
            />
            <ControlledUnifiedInput
              control={control}
              name="ttbPermit"
              label={t("importerFormModal.ttbPermitLabel")}
              placeholder={t("importerFormModal.ttbPermitPlaceholder")}
              wrapperClassName="col-span-2"
            />
            <ControlledUnifiedInput
              control={control}
              name="taxId"
              label="Tax ID"
              placeholder={t("importerFormModal.taxIdPlaceholder")}
            />
            <ControlledUnifiedInput
              control={control}
              name="einNumber"
              label="EIN"
              placeholder={t("importerFormModal.einNumberPlaceholder")}
            />
            <ControlledUnifiedInput
              control={control}
              name="duns"
              label="DUNS"
              placeholder={t("importerFormModal.dunsPlaceholder")}
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{t("importerFormModal.filesLabel")}</p>
            <FileDropzone
              availableFileTypes={IMPORTER_FILE_TYPES}
              allowedFileExtensions={IMPORTER_FILE_EXTENSIONS}
              currentFiles={isEdit ? currentFiles : []}
              maxFiles={IMPORTER_MAX_FILES}
              pendingFiles={pendingFiles}
              filesToRemove={filesToRemove}
              onPendingFilesChange={setPendingFiles}
              onFilesToRemoveChange={setFilesToRemove}
              disabled={isPending}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
